import React from "react";
import { Link } from "react-router-dom";
import { Seo } from "../../../components/Seo";

type Section = {
  title: string;
  description: string;
  capabilities: string[];
};

const sections: Section[] = [
  {
    title: "Billing and Payments",
    description:
      "Run the pharmacy counter with GST-ready billing and flexible payment collection.",
    capabilities: [
      "Point-of-sale billing and invoice generation",
      "Cash, card, UPI, and credit payments",
      "Customer invoices and outstanding balances",
    ],
  },
  {
    title: "Inventory, Batch, and Expiry",
    description:
      "Keep medicine stock accurate with batch-level tracking and expiry visibility.",
    capabilities: [
      "Real-time stock levels and reorder points",
      "Batch and manufacturing details",
      "Expiry tracking and low-stock alerts",
    ],
  },
  {
    title: "Purchasing and Vendors",
    description:
      "Plan purchases from sales velocity and stock, then manage orders through receipt.",
    capabilities: [
      "Suggested purchase orders from demand signals",
      "Purchase orders from draft to confirmation and receipt",
      "Vendor records, GSTIN, and credit limits",
    ],
  },
  {
    title: "GST and Accounting",
    description:
      "Keep pharmacy finances organized with GST calculations and day-to-day accounting.",
    capabilities: [
      "Chart of accounts and journal entries",
      "GST calculations and GST reports",
      "Sales, purchase, and payment visibility",
    ],
  },
  {
    title: "Customers and Reports",
    description:
      "Maintain customer records and review operational trends across the shop.",
    capabilities: [
      "Customer master data and credit limits",
      "Sales, purchases, and inventory value reports",
      "Payment-method and operational analytics",
    ],
  },
  {
    title: "Access and Shop Security",
    description:
      "Give each role the right access and keep shop data isolated where needed.",
    capabilities: [
      "Role-based access for admins, managers, accountants, and sales",
      "Shop-level data isolation for multi-branch setups",
      "Centralized controls for secure daily operations",
    ],
  },
];

const CimmpleRxDocsOverview: React.FC = () => {
  return (
    <div className="pt-24">
      <Seo
        title="CimmpleRx Docs | Cimmple"
        description="Overview of CimmpleRx pharmacy ERP for India: billing, batch inventory, purchases, GST, accounting, and team access."
      />

      <section className="pt-8 pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold text-accent-700 dark:text-accent-400 uppercase tracking-wide mb-3">
            CimmpleRx Docs
          </p>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-4 text-balance">
            CimmpleRx Overview
          </h1>
          <p className="text-lg text-slate-700 dark:text-dark-300 max-w-4xl text-balance">
            This page summarizes what CimmpleRx can do for medical shops, wholesalers, and agencies in India—useful if you
            are comparing solutions, onboarding a team, or explaining the product to colleagues. It is not a substitute for
            hands-on training in your own account.
          </p>
        </div>
      </section>

      <section className="pb-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card p-6 md:p-8">
            <h2 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-3">What you will find here</h2>
            <ul className="list-disc pl-5 space-y-2 text-slate-700 dark:text-dark-300">
              <li>A plain-language map of major areas: billing, inventory, purchasing, GST, and reporting.</li>
              <li>Typical pharmacy workflows and capabilities for retail and wholesale teams.</li>
              <li>Pointers to your Cimmple team or in-product resources when you need detailed, account-specific steps.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="pb-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {sections.map((section) => (
              <article key={section.title} className="card p-6 h-full">
                <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-2">{section.title}</h3>
                <p className="text-slate-700 dark:text-dark-300 mb-4">{section.description}</p>
                <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-700 dark:text-dark-300">
                  {section.capabilities.map((capability) => (
                    <li key={capability}>{capability}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card p-6 md:p-8">
            <h2 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-3">Need detailed how-tos?</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-4">
              Screen-by-screen guides, shop-specific setup, and training are best delivered inside your CimmpleRx
              environment or with your Cimmple contact—so instructions match your roles, branches, and go-live plan.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary">
                Contact Cimmple
              </Link>
              <Link to="/docs" className="btn-secondary">
                Back to Docs Hub
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CimmpleRxDocsOverview;
